export const BOT_CREATOR_HANDOFF_MIN_MS = 3000;
export const BOT_CREATOR_HANDOFF_QUIET_MS = 200;
export const BOT_CREATOR_HANDOFF_MAX_MS = 3500;
export const BOT_CREATOR_HANDOFF_DRIFT_PX = 12;
export const BOT_CREATOR_HANDOFF_STABLE_FRAMES = 3;

const USER_SCROLL_KEYS = new Set([
  'ArrowDown',
  'ArrowUp',
  'End',
  'Home',
  'PageDown',
  'PageUp',
  ' ',
]);

const isTextControl = (target) => target instanceof HTMLElement
  && (target.isContentEditable
    || target.tagName === 'INPUT'
    || target.tagName === 'TEXTAREA'
    || target.tagName === 'SELECT');

const documentTop = (windowObject, element) => (
  windowObject.scrollY + element.getBoundingClientRect().top
);

/**
 * Keep the newly revealed creator anchored while the existing bot cards finish
 * their initial asynchronous layout. This is deliberately action-scoped and
 * returns a cleanup function; it is not a general scroll-position authority.
 */
export const startBotCreatorScrollHandoff = ({
  contentAbove,
  input,
  section,
  windowObject = window,
}) => {
  if (!contentAbove || !input || !section) return () => {};

  const startedAt = windowObject.performance.now();
  const prefersReducedMotion = windowObject.matchMedia?.(
    '(prefers-reduced-motion: reduce)',
  ).matches === true;
  const scrollMargin = Number.parseFloat(
    windowObject.getComputedStyle(section).scrollMarginTop,
  ) || 0;
  let active = true;
  let animationFrame = null;
  let hardStopTimer = null;
  let minimumTimer = null;
  let observer = null;
  let lastLayoutChangeAt = startedAt;
  let lastSectionDocumentTop = documentTop(windowObject, section);
  let stableFrames = 0;

  const cleanup = () => {
    if (!active) return;
    active = false;
    if (animationFrame !== null) windowObject.cancelAnimationFrame(animationFrame);
    if (hardStopTimer !== null) windowObject.clearTimeout(hardStopTimer);
    if (minimumTimer !== null) windowObject.clearTimeout(minimumTimer);
    observer?.disconnect();
    windowObject.removeEventListener('wheel', cancelForUser, true);
    windowObject.removeEventListener('touchmove', cancelForUser, true);
    windowObject.removeEventListener('pointerdown', cancelForPointer, true);
    windowObject.removeEventListener('keydown', cancelForKeyboardScroll, true);
    windowObject.removeEventListener('resize', correctAfterViewportChange);
    windowObject.visualViewport?.removeEventListener(
      'resize',
      correctAfterVisualViewportChange,
    );
  };

  function cancelForUser() {
    cleanup();
  }

  function cancelForPointer(event) {
    if (section.contains(event.target)) return;
    cleanup();
  }

  function cancelForKeyboardScroll(event) {
    if (!USER_SCROLL_KEYS.has(event.key)) return;
    if (isTextControl(event.target) && !['PageDown', 'PageUp'].includes(event.key)) return;
    cleanup();
  }

  const alignSection = (behavior) => {
    if (!active) return;
    const top = Math.max(0, documentTop(windowObject, section) - scrollMargin);
    windowObject.scrollTo({ top, behavior });
  };

  const verifyStableFrame = () => {
    if (!active) return;
    const now = windowObject.performance.now();
    const sectionTop = section.getBoundingClientRect().top;
    const positionIsStable = Math.abs(sectionTop - scrollMargin)
      <= BOT_CREATOR_HANDOFF_DRIFT_PX;
    const layoutIsQuiet = now - lastLayoutChangeAt >= BOT_CREATOR_HANDOFF_QUIET_MS;
    stableFrames = positionIsStable && layoutIsQuiet ? stableFrames + 1 : 0;
    if (stableFrames >= BOT_CREATOR_HANDOFF_STABLE_FRAMES) {
      cleanup();
      return;
    }
    animationFrame = windowObject.requestAnimationFrame(verifyStableFrame);
  };

  const beginSettledCheck = () => {
    if (!active) return;
    stableFrames = 0;
    animationFrame = windowObject.requestAnimationFrame(verifyStableFrame);
  };

  const correctForLayoutChange = () => {
    if (!active) return;
    const nextDocumentTop = documentTop(windowObject, section);
    if (
      Math.abs(nextDocumentTop - lastSectionDocumentTop)
      < BOT_CREATOR_HANDOFF_DRIFT_PX
    ) return;
    lastSectionDocumentTop = nextDocumentTop;
    lastLayoutChangeAt = windowObject.performance.now();
    stableFrames = 0;
    alignSection('auto');
  };

  function correctAfterViewportChange() {
    if (!active) return;
    lastLayoutChangeAt = windowObject.performance.now();
    stableFrames = 0;
    alignSection('auto');
  }

  function correctAfterVisualViewportChange() {
    if (!active) return;
    const viewport = windowObject.visualViewport;
    if (!viewport) return;
    lastLayoutChangeAt = windowObject.performance.now();
    stableFrames = 0;
    const inputBounds = input.getBoundingClientRect();
    const visibleBottom = viewport.offsetTop + viewport.height - 16;
    if (inputBounds.bottom > visibleBottom) {
      windowObject.scrollTo({
        top: windowObject.scrollY + inputBounds.bottom - visibleBottom,
        behavior: 'auto',
      });
      return;
    }
    alignSection('auto');
  }

  animationFrame = windowObject.requestAnimationFrame(() => {
    if (!active) return;
    lastSectionDocumentTop = documentTop(windowObject, section);
    alignSection(prefersReducedMotion ? 'auto' : 'smooth');
    input.focus({ preventScroll: true });
    observer = new windowObject.ResizeObserver(correctForLayoutChange);
    observer.observe(contentAbove);
  });

  minimumTimer = windowObject.setTimeout(
    beginSettledCheck,
    BOT_CREATOR_HANDOFF_MIN_MS,
  );
  hardStopTimer = windowObject.setTimeout(() => {
    if (!active) return;
    alignSection('auto');
    cleanup();
  }, BOT_CREATOR_HANDOFF_MAX_MS);

  windowObject.addEventListener('wheel', cancelForUser, true);
  windowObject.addEventListener('touchmove', cancelForUser, true);
  windowObject.addEventListener('pointerdown', cancelForPointer, true);
  windowObject.addEventListener('keydown', cancelForKeyboardScroll, true);
  windowObject.addEventListener('resize', correctAfterViewportChange);
  windowObject.visualViewport?.addEventListener('resize', correctAfterVisualViewportChange);

  return cleanup;
};
